<template>
  <div class="privacy-policy-page-container page-container">
    <h1>{{ pageContent.title }}</h1>
    <p>{{ pageContent.lastUpdated }}</p>
    <p>{{ pageContent.description }}</p>

    <section
      v-for="section in pageContent.sections"
      :key="section.id"
      :id="section.id"
    >
      <h2>{{ section.title }}</h2>

      <template
        v-for="(paragraph, index) in section.paragraphs || []"
        :key="`${section.id}-paragraph-${index}`"
      >
        <p v-if="typeof paragraph === 'string'">
          {{ paragraph }}
        </p>
        <p v-else-if="paragraph.type === 'contact'">
          <span>{{ paragraph.intro }}</span>
          <a :href="`mailto:${paragraph.email}`">{{ paragraph.email }}</a>
          <span>{{ paragraph.connector }}</span>
          <RouterLink :to="paragraph.linkTo">{{ paragraph.linkLabel }}</RouterLink
          >{{ paragraph.ending }}
        </p>
      </template>

      <ul v-if="section.list && section.list.length">
        <li
          v-for="(item, itemIndex) in section.list"
          :key="`${section.id}-list-${itemIndex}`"
        >
          <template v-if="typeof item === 'string'">
            {{ item }}
          </template>
          <template v-else>
            <strong>{{ item.title }}</strong>{{ item.body }}
          </template>
        </li>
      </ul>

      <template v-if="section.extraLists && section.extraLists.length">
        <div
          v-for="(extra, extraIndex) in section.extraLists"
          :key="`${section.id}-extra-${extraIndex}`"
        >
          <p>{{ extra.intro }}</p>
          <ul>
            <li
              v-for="(entry, entryIndex) in extra.items"
              :key="`${section.id}-extra-${extraIndex}-item-${entryIndex}`"
            >
              {{ entry }}
            </li>
          </ul>
        </div>
      </template>

      <template v-if="section.closing && section.closing.length">
        <p
          v-for="(closingParagraph, closingIndex) in section.closing"
          :key="`${section.id}-closing-${closingIndex}`"
        >
          {{ closingParagraph }}
        </p>
      </template>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { privacyPolicyContent } from '@/content/privacyPolicy';

const { locale } = useI18n();

const pageContent = computed(
  () => privacyPolicyContent[locale.value] ?? privacyPolicyContent['en-US']
);
</script>

<style scoped>
/* Using a common class or copying styles from TermsOfServicePage.vue for consistency */
.page-container { /* Common class for static pages */
  max-width: 800px;
  margin: 20px auto;
  padding: 25px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  line-height: 1.7;
  color: #34495e; /* Darker grey for text */
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.page-container h1 {
  text-align: center;
  margin-bottom: 15px;
  color: #2c3e50; /* Even darker for main title */
  font-size: 2em;
  font-weight: 600;
}
.page-container > p:nth-of-type(1) { /* For "Last updated date" */
  text-align: center;
  font-size: 0.9em;
  color: #7f8c8d; /* Lighter grey for meta text */
  margin-bottom: 10px;
}
.page-container > p:nth-of-type(2) { /* For introductory paragraph */
  text-align: justify;
  font-size: 1em;
  color: #555;
  margin-bottom: 25px;
}
.page-container h2 {
  margin-top: 35px;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 2px solid #ecf0f1; /* Lighter border */
  color: #16a085; /* Theme color for section titles */
  font-size: 1.6em;
  font-weight: 500;
}
.page-container p, .page-container ul {
  margin-bottom: 12px;
  text-align: justify;
  color: #34495e;
}
.page-container ul {
  padding-left: 30px; /* Standard indent for lists */
}
.page-container li {
  margin-bottom: 8px;
}
.page-container section {
  margin-bottom: 25px;
}
.page-container a {
  color: #2980b9; /* Standard link blue */
  text-decoration: none;
}
.page-container a:hover {
  text-decoration: underline;
}
</style>
